// Import React hooks for state and lifecycle management
import { useState, useEffect } from 'react';
// Axios for HTTP requests
import axios from 'axios';
// React Router hooks for navigation and location
import { useNavigate, useLocation } from 'react-router-dom';
// Custom hook to access authenticated user and refresh data
import { useAuth } from '../hooks/useAuth';
// Type definitions for User
import type { User } from '../types';

// Component for displaying and updating the user's profile
function ProfilePage() {
  // Get current user, loading state, and refresh function
  const { user, isLoading, refreshUser } = useAuth();
  // Local state for the form data (editable profile fields)
  const [formData, setFormData] = useState<User | null>(null);
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  // State for success messages
  const [success, setSuccess] = useState<string | null>(null);
  // State to track hover on profile photo
  const [isHovering, setIsHovering] = useState(false);
  // React Router hooks for navigation and getting previous location
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize formData when user is loaded, or redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (user && !formData) {
      // Initialize photo URL
      const photoUrl = user.photo ? `${user.photo.startsWith('http') ? '' : 'http://localhost:8000'}${user.photo}` : null;
      // Populate formData from user object
      setFormData({
        id: user.id,
        username: user.username,
        role: user.role,
        email: user.email || '',
        real_name: user.real_name || '',
        photo: photoUrl,
      });
    }
  }, [user, isLoading, navigate, formData]);
  // Handle profile form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;
    try {
      // Fetch CSRF token for secure PUT request
      const response = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = response.data.csrfToken;
      // Exclude photo from update payload
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { photo, ...updateData } = formData;
      // Send PUT request to update user profile
      await axios.put('/elearning/api/users/me/', updateData, {
        withCredentials: true,
        headers: { 'X-CSRFToken': csrfToken },
      });
      // Show success message and refresh user data
      setSuccess('Profile updated successfully');
      setError(null);
      setTimeout(() => setSuccess(null), 5000);
      refreshUser();
    } catch (err: any) {
      console.error('ProfilePage: Error updating profile=', err);
      setError(err.response?.data?.message || 'Failed to update profile.');
      setSuccess(null);
      setTimeout(() => setError(null), 5000);
    }
  };
  // Navigate to photo upload page
  const handlePhotoChange = () => {
    navigate('/profile/upload-photo');
  };
  // Handle profile photo deletion
  const handlePhotoDelete = async () => {
    try {
      // Fetch CSRF token for secure DELETE request
      const response = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = response.data.csrfToken;
      // Send DELETE request to remove photo
      await axios.delete('/elearning/api/users/me/photo/', {
        withCredentials: true,
        headers: { 'X-CSRFToken': csrfToken },
      });
      // Update local formData and show success message
      setFormData((prev) => (prev ? { ...prev, photo: null } : prev));
      setSuccess('Profile photo deleted successfully');
      setError(null);
      setTimeout(() => setSuccess(null), 5000);
      refreshUser();
    } catch (err: any) {
      console.error('ProfilePage: Error deleting photo=', err);
      setError(err.response?.data?.message || 'Failed to delete photo.');
      setSuccess(null);
      setTimeout(() => setError(null), 5000);
    }
  };
  // Navigate back to previous page or homepage
  const handleBack = () => {
    navigate(location.state?.from || '/');
  };
  // Render loading spinner while user data is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Return nothing if user or formData is not available
  if (!user || !formData) {
    return null;
  }
  return (
  <div className="min-h-screen bg-gray-50 font-sans">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-3xl">
      {/* Top navigation buttons */}
      <div className="flex justify-end mb-6 space-x-4">
        <button
          onClick={handleBack}
          className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Home
        </button>
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6">
          <h1 className="text-2xl font-bold">Your Profile</h1>
        </div>

        {/* Card content */}
        <div className="p-6 space-y-6">
          {/* Errors */}
          {error && (
            <div className="flex items-center p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          )}

          {/* Success messages */}
          {success && (
            <div className="flex items-center p-4 bg-green-50 text-green-700 rounded-lg shadow-sm">
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {success}
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile photo */}
            <div className="flex-shrink-0 relative">
              <div
                className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center cursor-pointer"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {formData.photo ? (
                  <img
                    src={`${formData.photo}?t=${Date.now()}`}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => console.error('ProfilePage: Image load error', e)}
                  />
                ) : (
                  <svg
                    className="h-16 w-16 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                )}

                {isHovering && formData.photo && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center gap-2 z-10">
                    <button
                      onClick={handlePhotoChange}
                      className="bg-blue-600 text-white py-1 px-3 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                    >
                      Change
                    </button>
                    <button
                      onClick={handlePhotoDelete}
                      className="bg-red-600 text-white py-1 px-3 rounded-lg text-sm hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {!formData.photo && (
                <button
                  onClick={handlePhotoChange}
                  className="mt-4 w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Upload Photo
                </button>
              )}
            </div>

            {/* Profile form */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-4">
              <div>
                <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-1">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={formData.username}
                  disabled
                  className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 font-semibold cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email ?? ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold"
                />
              </div>

              <div>
                <label htmlFor="real_name" className="block text-sm font-semibold text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  id="real_name"
                  type="text"
                  value={formData.real_name ?? ''}
                  onChange={(e) => setFormData({ ...formData, real_name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold"
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Update Profile
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Back to Home
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
);

}
// Export the ProfilePage component as default
export default ProfilePage;
